import { Directive, ElementRef, HostListener, Optional, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
	selector: 'input[matInput], textarea[matInput], input[formControlName], textarea[formControlName], input[ngModel], textarea[ngModel]'
})
export class TrimInputDirective implements OnInit, OnDestroy {
	private highlightElement: HTMLElement | null = null;
	private container: HTMLElement | null = null;
	private originalParent: HTMLElement | null = null;

	constructor(
		private elementRef: ElementRef,
		private renderer: Renderer2,
		@Optional() private ngControl: NgControl
	) { }

	ngOnInit(): void {
		this.setupHighlight();
	}

	private setupHighlight(): void {
		const element = this.elementRef.nativeElement;
		this.originalParent = element.parentNode;

		this.container = this.renderer.createElement('div');
		this.renderer.setStyle(this.container, 'position', 'relative');
		this.renderer.setStyle(this.container, 'display', 'inline-block');
		this.renderer.setStyle(this.container, 'width', '100%');

		this.highlightElement = this.renderer.createElement('div');
		this.renderer.setStyle(this.highlightElement, 'position', 'absolute');
		this.renderer.setStyle(this.highlightElement, 'top', '0');
		this.renderer.setStyle(this.highlightElement, 'left', '0');
		this.renderer.setStyle(this.highlightElement, 'right', '0');
		this.renderer.setStyle(this.highlightElement, 'bottom', '0');
		this.renderer.setStyle(this.highlightElement, 'pointer-events', 'none');
		this.renderer.setStyle(this.highlightElement, 'font-family', 'inherit');
		this.renderer.setStyle(this.highlightElement, 'font-size', 'inherit');
		this.renderer.setStyle(this.highlightElement, 'line-height', 'inherit');
		this.renderer.setStyle(this.highlightElement, 'white-space', 'pre');
		this.renderer.setStyle(this.highlightElement, 'overflow', 'hidden');
		this.renderer.setStyle(this.highlightElement, 'z-index', '1');
		this.renderer.setStyle(this.highlightElement, 'color', 'transparent');
		this.renderer.setStyle(this.highlightElement, 'background', 'transparent');

		const computedStyle = getComputedStyle(element);
		this.renderer.setStyle(this.highlightElement, 'padding', computedStyle.padding);
		this.renderer.setStyle(this.highlightElement, 'border', computedStyle.border);
		this.renderer.setStyle(this.highlightElement, 'border-color', 'transparent');
		this.renderer.setStyle(this.highlightElement, 'box-sizing', computedStyle.boxSizing);

		if (this.originalParent) {
			this.renderer.insertBefore(this.originalParent, this.container, element);
			this.renderer.appendChild(this.container, element);
			this.renderer.appendChild(this.container, this.highlightElement);

			this.renderer.setStyle(element, 'position', 'relative');
			this.renderer.setStyle(element, 'z-index', '2');
			this.renderer.setStyle(element, 'background', 'transparent');
		}
	}

	@HostListener('input', ['$event'])
	@HostListener('keyup', ['$event'])
	@HostListener('focus', ['$event'])
	onInput(event: Event): void {
		this.updateHighlight();
	}

	@HostListener('blur', ['$event'])
	onBlur(event: Event): void {
		const element = this.elementRef.nativeElement;
		const trimmedValue = element.value?.trim();

		if (element.value !== trimmedValue) {
			element.value = trimmedValue;

			if (this.ngControl && this.ngControl.control) {
				this.ngControl.control.setValue(trimmedValue);
				this.ngControl.control.markAsTouched();
			}

			element.dispatchEvent(new Event('input', { bubbles: true }));
		}

		this.clearHighlight();
	}

	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent): void {
		setTimeout(() => {
			const element = this.elementRef.nativeElement;
			const trimmedValue = element.value?.trim();

			if (element.value !== trimmedValue) {
				element.value = trimmedValue;

				if (this.ngControl && this.ngControl.control) {
					this.ngControl.control.setValue(trimmedValue);
				}

				element.dispatchEvent(new Event('input', { bubbles: true }));
			}

			this.updateHighlight();
		}, 0);
	}

	private updateHighlight(): void {
		if (!this.highlightElement) return;

		const element = this.elementRef.nativeElement;
		const value = element.value || '';

		const leadingSpaces = value.match(/^\s+/);
		const trailingSpaces = value.match(/\s+$/);

		if (leadingSpaces || trailingSpaces) {
			let highlightedText = value;

			if (leadingSpaces) {
				const spaces = leadingSpaces[0];
				highlightedText = highlightedText.replace(
					/^\s+/,
					`<span style="background-color: #ff4444; opacity: 0.8;">${spaces}</span>`
				);
			}

			if (trailingSpaces) {
				const spaces = trailingSpaces[0];
				highlightedText = highlightedText.replace(
					/\s+$/,
					`<span style="background-color: #ff4444; opacity: 0.8;">${spaces}</span>`
				);
			}

			this.renderer.setProperty(this.highlightElement, 'innerHTML', highlightedText);
		} else {
			this.clearHighlight();
		}
	}

	private clearHighlight(): void {
		if (this.highlightElement) {
			this.renderer.setProperty(this.highlightElement, 'innerHTML', '');
		}
	}

	ngOnDestroy(): void {
		if (this.container && this.originalParent) {
			const element = this.elementRef.nativeElement;

			this.renderer.insertBefore(this.originalParent, element, this.container);
			this.renderer.removeChild(this.originalParent, this.container);

			this.renderer.removeStyle(element, 'position');
			this.renderer.removeStyle(element, 'z-index');
			this.renderer.removeStyle(element, 'background');
		}
	}
}
